import runtimeContext from '../environments/runtime-context';
import DetailedError from '../services/Error';
import {PrimitiveOrArray} from '../app-types';
import {Primitive} from 'utility-types';
import {flattenDeep} from 'lodash';
import {HibuTag, SearchSource, SearchSourceType} from '../search/search-types';
import {getCurrentLanguage} from '../i18n-service';
import {setUserId} from '../settings/duck/settings-localstorage-service';

export const timestampFormat = 'YYYY-MM-DD HH:mm Z';
export const dateFormat = 'YYYY-MM-DD';

/**
 * Tries grabbing a container with specified className and then scrolling to top.
 * Used for scroll back to top at lists before executing a search task.
 * Otherwise, the infinite scroll list would load all results until scrolled distance.
 *
 * @param elementClass the className of the container
 */
export const scrollToTop = (elementClass: string): number | undefined => {
    const container = runtimeContext.document().getElementsByClassName(elementClass)?.[0];
    if (!container) {
        return;
    }
    container.scrollTop = 0;
};

/**
 * Check if this is a touch device and returns true if so and false if not.
 * source: https://coderwall.com/p/i817wa/one-line-function-to-detect-mobile-devices-with-javascript
 *
 * @returns {boolean}
 */
export const isTouchDevice = (): boolean => runtimeContext.isTouchDevice();

/**
 * Alters the window location url without reloading the page.
 * But only if the current location does not match the specified url.
 *
 * @param url the url to change
 */
export const setWindowLocation = (url: string): void => {
    runtimeContext.setAddressbarUrl(url);
};

/**
 * Deep merges two objects.
 *
 * @param target the target object
 * @param source the source object
 * @returns the merged object
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const mergeObjects = (target: any, source: Record<any, any>): any => {
    const isObject = (obj) => obj && typeof obj === 'object';

    if (!isObject(target) || !isObject(source)) {
        return source;
    }

    Object.keys(source).forEach((key: string) => {
        const targetValue: any = target[key];
        const sourceValue: any = source[key];

        if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
            target[key] = targetValue.concat(sourceValue);
        } else if (isObject(targetValue) && isObject(sourceValue)) {
            target[key] = mergeObjects(Object.assign({}, targetValue), sourceValue);
        } else {
            target[key] = sourceValue;
        }
    });
    return target;
};

/**
 * Brings a given path into format: let path = 'example.path';
 *
 * @param path the string to be normalized
 * @returns the normalized string
 */
export const normalizeTranslationPath = (path: string): string => {
    return path
        .replace(/\.+$/, '') // remove trailing '.' char
        .replace(/^\./, ''); // remove leading '.' char
};

/**
 * Gets array value at index position idx -- if it exists --
 * and undefined otherwise
 *
 * @param arr the String array
 * @param idx the index position
 * @returns the extracted string or undefined
 */
export const stringArrayGetAtIdxIfDefined = (arr: string[], idx: number): string => {
    return arr && arr[idx];
};

/**
 * Gets array value at index position idx -- if it exists --
 * and an empty string otherwise
 *
 * @param arr the array
 * @param idx the index position
 * @returns the extracted string or the default string
 */
export const arrayGetAtIdxOrEmptyString = (arr: string[], idx: number): string => {
    return arrayGetAtIdxOrDefault(arr, idx, '');
};

/**
 * Gets array value at index position idx -- if it exists --
 * and some default string otherwise
 *
 * @param arr the array
 * @param idx the index position
 * @param defaultVal the default string
 * @returns the extracted string or the default string
 */
export const arrayGetAtIdxOrDefault = (arr: string[], idx: number, defaultVal: string): string => {
    return (arr && arr[idx]) || defaultVal;
};

/**
 * Returns the error message from a DetailedError.
 *
 * @param {DetailedError} error the error to get the error message from.
 */
export const getErrorMessage = (error: DetailedError): string => {
    if (error && error.detail) {
        if (error.detail.errorMessage) {
            // generic errors
            return error.detail.errorMessage;
        } else if (error.detail.errorData) {
            // errors with JSON body (like from hibuServiceException)
            return error.detail.errorData.message;
        }
    }
    console.error('Could not get error from: ' + JSON.stringify(error));
    return generalErrorMessage;
};

/**
 * Takes a numerical value and converts it into Euros.
 * Number formatting is based on the locales of the users browser.
 *
 * @param currency the num value to be converted
 * @returns the currency representation of the given value
 */
export const toEuro = (currency: number): string => {
    if (!currency) {
        return '';
    }
    const browserLocales = navigator.language || 'en-US';
    return currency.toLocaleString(browserLocales, {
        style: 'currency',
        currency: 'EUR',
    });
};

/**
 * Takes a string and returns the extension (without the dot).
 *
 * Examples:
 * ""                            -->   ""
 * "name"                        -->   ""
 * "name.txt"                    -->   "txt"
 * ".htpasswd"                   -->   ""
 * "name.with.many.dots.myext"   -->   "myext"
 *
 * @param fileName the file name to extract the extension from
 * @returns the extension or '' if none available
 */
export const extractFileExtension = (fileName = ''): string =>
    fileName.slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2);

export const isEmailAddress = (s: string): boolean => {
    const emailPattern = /\S+@\S+\.\S+/;
    return emailPattern.test(s);
};

/**
 * Takes an object or object array and transforms it into a deeply flattened array.
 *
 * Examples:
 * ""                                 --> [""]
 * "hello"                            --> ["hello"]
 * [""]                               --> [""]
 * ["a", "b"]                         --> ["a","b"]
 * [[""]]                             --> [""]
 * [["a"],["b"]]                      --> ["a","b"]
 * [[["a"],["b"]],"c"]                --> ["a","b","c"]
 * [[["a"],["b"],["c"]]]              --> ["a","b","c"]
 * @param possiblyArray a primitive type, possibly an array
 * @returns the deeply flattened new array
 */
export const flattenToArray = (possiblyArray: PrimitiveOrArray): Primitive[] => {
    return flattenDeep([possiblyArray]);
};

/**
 * Takes an object or object array, transforms it into a deeply flattened array and then string joins
 * them using the given separator.
 *
 * Examples (join using -):
 * ""                                 --> ""
 * "hello"                            --> "hello"
 * [""]                               --> ""
 * [[""]]                             --> ""
 * ["a","b"]                          --> "a-b"
 * [["a"],["b"]]                      --> "a-b"
 * [[["a"],["b"]],"c"]                --> "a-b-c"
 * [[["a"],["b"]],["c"]]              --> "a-b-c"
 * [[["a"],["b"],["c"]]]              --> "a-b-c"
 * @param separator an object, possibly an array
 * @param possiblyArray the separator used to join the elements or ", " if none is given
 * @returns the deeply flattened new array joined with the given separator
 */
export const flattenAndJoin = (possiblyArray: PrimitiveOrArray, separator = ', '): string => {
    return flattenToArray(possiblyArray).join(separator);
};

// Functions for tests
export type MockedData<T> = {
    [P in keyof T]?: MockedData<T[P]>;
};

export function mockData<T>(instance: MockedData<T>): T {
    return instance as any;
}

export const generalErrorMessage =
    'An unknown error occurred. Please try again later. We apologize for the inconvenience.';

export const defaultQueryDelimiter = '|';

/**
 * Parses a query string and returns it in object form.
 *
 * @param queryString the query string to parse (e. g. "?key1=value1&key2=value2")
 * @param keyTransform which function should be applied on each of the keys (makes all keys lowercase by default)
 * @param valueTransform which function should be applied on each of the values
 * @param keyFilter will remove all key/value pairs which DON'T match this condition on the key AFTER transformation
 * @param valueFilter will remove all key/value pairs which DON'T match this condition on the value(s) AFTER transformation
 * @param splitMultipleValues if set to true, it will split up the values by the {@link defaultQueryDelimiter} and return an array of values instead (defaults to false)
 * @returns {@code Record<string, Primitive>} if {@code splitMultipleValues} is false, {@code Record<string, Primitive[]>} if {@code splitMultipleValues} is true
 */
export function getObjectFromQueryString(
    queryString: string,
    keyTransform: (key: string) => string = (key: string) => key.toLowerCase(),
    valueTransform: (value: string) => any = (value: string) => value,
    keyFilter?: (key: string) => boolean,
    valueFilter?: (value: string) => boolean,
    splitMultipleValues = false
): Record<string, Primitive | Primitive[]> {
    const obj: Record<string, Primitive | Primitive[]> = {};
    if (!queryString) {
        return obj;
    }
    const query = queryString.indexOf('?') === 0 ? queryString.substring(1) : queryString;
    const vars = query.split('&');

    for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split('=');
        let key = pair[0];
        let value = pair[1];
        key = keyTransform(decodeURIComponent(key));
        value = decodeURIComponent(value);

        if (keyFilter && !keyFilter(key)) {
            continue;
        }

        if (splitMultipleValues) {
            const values = value.split(defaultQueryDelimiter).map(valueTransform);
            if (!valueFilter || values.every(valueFilter)) {
                obj[key] = values;
            }
        } else {
            value = valueTransform(value);
            if (!valueFilter || valueFilter(value)) {
                obj[key] = value;
            }
        }
    }

    return obj;
}

export function getQueryStringFromObject(
    obj: Record<string, PrimitiveOrArray>,
    delimiter = defaultQueryDelimiter
): string {
    let queryString = '';

    if (obj) {
        queryString = Object.keys(obj)
            .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(flattenAndJoin(obj[k], delimiter))}`)
            .join('&');
    }

    if (queryString) {
        queryString = '?' + queryString;
    }

    return queryString;
}

/**
 * Returns whether or not there are duplicates in {@code array}.
 *
 * NOTE: This only performs a shallow comparison! It can't determine if two duplicated objects are present, unless
 * they have the same reference.
 *
 * @param array to check for duplicates
 */
export const hasDuplicates = (array: any[]): boolean => new Set(array).size !== array.length;

/**
 * Returns {@code str} parsed as a number, if the string CANNOT be parsed as a number, it will return {@code undefined}.
 * @param str the string to parse as a number
 */
export function parseNumber(str: string): number | undefined {
    if (!str || str.trim().length === 0 || str.startsWith('+') || str.startsWith(' ') || str.endsWith(' ')) {
        return undefined;
    }
    const num = Number(str);
    if (isNaN(num)) {
        return undefined;
    } else {
        return num;
    }
}

/**
 * Returns whether or not {@code percentage} is a valid percentage or not.
 * @param percentage to check for validity
 */
export function isPercentage(percentage: number): boolean {
    return percentage >= 0 && percentage <= 100;
}

/**
 * Parses the percentage in a string which may contain a % at the end to a number or returns undefined, if {@code value}
 * is not a percentage.
 *
 * @param value string to parse as a percentage
 */
export function parsePercentage(value: string): number | undefined {
    // 0 should be allowed as well
    if (value === undefined || value === null || value.length === 0) {
        return undefined;
    }
    if (value.endsWith('%')) {
        // cut off % at the end of the range
        const percentageSplit = value.split('%');
        // make sure there is only one % symbol
        if (percentageSplit.length === 2 && percentageSplit[1].length === 0) {
            value = percentageSplit[0];
        } else {
            return undefined;
        }
    }
    const percentage = parseNumber(value);
    if (percentage !== undefined && isPercentage(percentage)) {
        // 0 should be allowed as well
        return percentage;
    }
}

/**
 * Validates whether or not {@code value} is a percentage or a range in the form of xx.yy(%) or xx.yy-xx.yy(%) with
 * the % sign being optional, but not allowed on the first value in case of a range.
 *
 * @param value string to validate
 */
export function isPercentageOrRange(value: string): boolean {
    if (value === undefined || value === null) {
        return false;
    }
    const rangeOrValue = value.split('-');
    if (rangeOrValue.length === 2) {
        // is range
        const percentage1 = rangeOrValue[0];
        const percentage2 = rangeOrValue[1];
        if (percentage1.endsWith('%')) {
            // range may only contain % at the very end
            return false;
        }
        const percentage2Number = parsePercentage(percentage2); // may contain % sign
        const percentage1Number = parseNumber(percentage1); // must NOT contain % sign
        // check for undefined since 0 is allowed
        if (percentage1Number === undefined || !isPercentage(percentage1Number) || percentage2Number === undefined) {
            // parsePercentage already checked isPercentage here
            return false;
        }
        // make sure the first number is smaller than the second number
        return percentage1Number < percentage2Number;
    } else if (rangeOrValue.length === 1) {
        // is percentage
        const percentage = rangeOrValue[0];
        return parsePercentage(percentage) !== undefined;
    }
    return false;
}

export function isEmptyString(s: string | undefined | null): boolean {
    return s === null || s === undefined || s === '';
}

export function isEmptyArray(array: any[] | undefined | null): boolean {
    return !array || array.length === 0;
}

export enum Keyname {
    ARROW_UP = 'ArrowUp',
    ARROW_DOWN = 'ArrowDown',
    ENTER = 'Enter',
    ESCAPE = 'Escape',
}

/**
 * Feature detection to check if the {@link AbortController} can be used in the current browser.
 */
export function isAbortControllerAvailable(): boolean {
    return 'AbortController' in window;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function isAbortError(error: any): boolean {
    return error.name === 'AbortError';
}

export const generateRandomSortSeed = (): string => {
    const randomValue = Math.floor(Math.random() * 99999);
    const randomSort = 'random_' + randomValue;
    if (localStorageSupported()) {
        localStorage.setItem('randomSortSeed', randomSort);
    }
    return randomSort;
};

export const localStorageSupported = (): boolean => {
    // from https://github.com/Modernizr/Modernizr/blob/master/feature-detects/storage/localstorage.js
    const mod = 'modernizr';
    try {
        localStorage.setItem(mod, mod);
        localStorage.removeItem(mod);
        return true;
    } catch (e) {
        return false;
    }
};

export const storeToLocalStorage = (key: string, value: string) => {
    if (localStorageSupported()) {
        localStorage.setItem(key, value);
    }
};

export const getFromLocalStorageOrDefault = (key: string): string => {
    let userId = getFromLocalStorage(key);
    if (!userId) {
        userId = generateRandomUserId();
        setUserId(userId); //localstorage
    }
    return userId;
};

const generateRandomUserId = (): string => {
    const randomValue = Math.floor(Math.random() * 999);
    return 'user_' + randomValue + Date.now();
};

export const getFromLocalStorage = (key: string): string | null =>
    localStorageSupported() ? localStorage.getItem(key) : null;

export const isSearchInFavorites = (sources: SearchSource[]) => {
    return sources.some(
        (source) => source.active && source.name === SearchSourceType.BOOKMARKS
    );
};

export const getHibuThemeColor = (): string => {
    return getComputedStyle(document.documentElement).getPropertyValue('--hibuThemeColor');
};

export const getHibuTextLightColor = (): string => {
    return getComputedStyle(document.documentElement).getPropertyValue('--hibuTextLightColor');
};

/**
 * Adds an element created by {@code elementFn} between all the elements in {@code array}.
 *
 * NOTE: This will change the original array!
 *
 * ### Example:
 * ```js
 * const array = ["a", "b", "c", "d"];
 * addElementBetweenElements(array, () => "|"); // ["a", "|", "b", "|", "c", "|", "d"]
 * ```
 * @param array in which to add elements created by {@code elementFn}
 * @param elementFn to create the elements to add between the elements in {@code array}
 */
export function addElementBetweenElements<T>(array: T[], elementFn: (key: string) => T): T[] {
    let i = 1;
    while (i < array.length) {
        array.splice(i, 0, elementFn('sep_' + i));
        i += 2;
    }
    return array;
}

export const formatDateShort = (value: number | string | Date): string => {
    return formatDate(value, {
        year: 'numeric',
        month: 'short',
    });
};

const formatDate = (value: number | string | Date, options: Intl.DateTimeFormatOptions): string => {
    const date = new Date(value);
    const dtf = new Intl.DateTimeFormat(getCurrentLanguage(), options);
    return dtf.format(date);
};

/** Converts a string to a readable field name
 * examples: 'fieldTitle.metadata.filesize_long' into 'Filesize'
 * 'tags.projectName.labels.*' into 'Projectname'
 * */
export const buildDefaultFieldTitle = (fieldName: string): string => {
    const parts: string[] = fieldName.split('.');
    let readableFieldName: string = (parts[parts.length - 1] === '*' && parts[parts.length - 2] === 'labels') ? parts[parts.length - 3] : parts[parts.length - 1];
    readableFieldName = removeSuffix(readableFieldName);
    readableFieldName = readableFieldName.charAt(0).toUpperCase() + readableFieldName.slice(1).toLowerCase();
    readableFieldName = readableFieldName.replace(/_/, ' ');
    return readableFieldName;
};

export const removeSuffix = (s: string): string => {
    return s.replace(
        /_(?:multi_string|string|tokenized_string|text|long|bool|date|path|double|dirPath)(?:_de|_en)?$/,
        ''
    );
};

/**
 * Get OpenStreetMap url
 * composed from the coordinates in the Hibu tag and the appropriate zoom level
 */
export const getOpenstreetmapUrl = (tag: HibuTag): string => {
    // @ts-ignore
    const latitude = tag.extra.coordinates[0];
    // @ts-ignore
    const longitude = tag.extra.coordinates[1];
    // @ts-ignore
    const area = Number(tag.extra.area);
    const zoom = deriveZoomLevel(area);
    return `https://www.openstreetmap.org/#map=${zoom}/${latitude}/${longitude}`;
};

/**
 * derive optimal zoom level from area (in km²):
 * min zoom level: 3
 * max zoom level: 17   (also the default return value if area is missing or 0 or no number)
 * in the following: x=log10(area)
 * if x<=3 -> zoom = round( 2.14*(3-x) + 10 )
 * if x>=3  -> zoom = round( -1.44*(x-3) + 10 )
 */
const deriveZoomLevel = (area: number): number => {
    if (!area || area <= 0) {
        return 17; // default zoom level if area value is missing or <=0 (which would not work with the log10)
    }
    const logArea = Math.log10(area);
    const zoomLevelFloat = logArea <= 3 ? 2.14 * (3.0 - logArea) + 10.0 : -1.44 * (logArea - 3.0) + 10.0;
    const zoomLevelInt = Math.round(zoomLevelFloat);
    return Math.max(3, Math.min(17, zoomLevelInt));
};

interface ObjectWithId {
    id: string;
}

/**
 * Finds and returns the different ids for the new array. Example:
 * const oldArray = [{id: 'a'}, {id: 'b'}, {id: 'c'}] // a and b are identical
 * const newArray = [{id: 'a'}, {id: 'd'}, {id: 'b'}] // c has been replaced by d
 * findChangedId(oldArray, newArray) === ['d']        // function returns d
 */
export function findNewIdOfChangedTag<T extends ObjectWithId>(oldArray: Array<T>, newArray: Array<T>): Array<string> {
    const oldIds = oldArray.map(({id}) => id);
    const newIds = newArray.map(({id}) => id);
    return newIds.filter((x) => !oldIds.includes(x));
}

/**
 * Computes the new rotation of a document based on its current rotation and a rotation change.
 * This function ensures that the resulting rotation is always one of the valid values: 0, 90, 180, or 270 degrees.
 * It handles wrap-around cases, such as rotating from 0 to -90 degrees (which results in 270 degrees).
 */
export function computePageRotation(currentRotation: number, rotationChange: number): number {
    let newRotation = (currentRotation + rotationChange) % 360;
    if (newRotation < 0) {
        newRotation += 360;
    }
    return newRotation;
}
